/**
 * Ask Dink route catalogue (DNKADM-0434 U1).
 *
 * The router (`src/router.tsx`) is the one source of truth for screens: every
 * routable screen carries `handle: { askDink: { title, description } }`. This module
 * walks that tree and builds the list the caller sends with each question
 * (`POST /v2/admin/assistant/ask` `routes`, DNKAPI-0627), so the answer can only link
 * to screens this session can actually open.
 *
 * What counts as a screen: a LEAF route under the `AuthGate` shell that is not a
 * `<Navigate>` redirect, not the `*` fallback, and not a detail page (no `:param`
 * anywhere on its path — the list carries id-free paths only, e.g. `/members`, never
 * `/members/123`). Layout parents (routes with children) carry no metadata; their
 * index child carries it under the parent's path. A screen that only forwards
 * somewhere (an index gate or an OAuth return) opts out with `askDink: false`.
 *
 * Who sees what — the same gates the console uses, never a parallel list:
 *  - a route under `<RequireAdmin>` is offered only when `canOpenAdminRoutes` (the
 *    guard's own predicate) passes;
 *  - for a STAFF session, a route whose nav leaf `staffSeesLeaf` rejects
 *    (`navModel.ts` — the one RUN/SET UP rule, DNKADM-0375) is withheld too.
 * Presentation only: dink-api re-checks the caller on every call and drops any link
 * that was not in the list it was sent.
 */
import { isValidElement } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { AuthGate } from '@/auth/AuthGate';
import { RequireAdmin } from '@/components/RequireAdmin';
import { canOpenAdminRoutes } from '@/components/requireAdminAccess';
import { ALL_NAV_LEAVES, ALL_NAV_PATHS, activeNavPath, staffSeesLeaf } from '@/components/navModel';

/** One screen's Ask Dink metadata: a short title and one plain-words line. */
export interface AskDinkMeta {
  title: string;
  description: string;
}

/** One entry of the `routes` list sent to the assistant. */
export interface AskDinkRoute {
  path: string;
  title: string;
  description: string;
}

/** The session facts the catalogue filters on (all from `CurrentOperator` + Front Desk). */
export interface AskDinkAccess {
  isAdmin: boolean;
  isStaffTier: boolean;
  frontDeskView: boolean;
}

/** The contract's cap on `routes` (DNKAPI-0627). */
export const ASK_DINK_ROUTE_LIMIT = 300;

/** A routable screen found in the tree, before any access filtering. */
export interface CatalogueScreen {
  path: string;
  /** The route's `handle.askDink`: metadata, `false` (deliberately not a screen), or missing. */
  meta: AskDinkMeta | false | undefined;
  /** True when the route sits under `<RequireAdmin>`. */
  adminOnly: boolean;
}

function readMeta(route: RouteObject): AskDinkMeta | false | undefined {
  const handle: unknown = route.handle;
  if (typeof handle !== 'object' || handle === null || !('askDink' in handle)) return undefined;
  const meta = (handle as { askDink: unknown }).askDink;
  if (meta === false) return false;
  if (
    typeof meta === 'object' &&
    meta !== null &&
    typeof (meta as AskDinkMeta).title === 'string' &&
    typeof (meta as AskDinkMeta).description === 'string'
  ) {
    return meta as AskDinkMeta;
  }
  return undefined;
}

function joinPath(base: string, segment: string): string {
  if (segment.startsWith('/')) return segment;
  return `${base.endsWith('/') ? base : `${base}/`}${segment}`;
}

/**
 * Every routable, non-detail screen inside the authenticated shell, in tree order.
 * Exported for the metadata coverage guard; product code uses {@link buildAskDinkRoutes}.
 */
export function catalogueScreens(routes: readonly RouteObject[]): CatalogueScreen[] {
  const out: CatalogueScreen[] = [];
  const walk = (
    list: readonly RouteObject[],
    parentPath: string,
    inShell: boolean,
    adminOnly: boolean,
    detail: boolean,
  ) => {
    for (const route of list) {
      const type = isValidElement(route.element) ? route.element.type : undefined;
      const path = route.index === true ? parentPath : joinPath(parentPath, route.path ?? '');
      const nextShell = inShell || type === AuthGate;
      const nextAdmin = adminOnly || type === RequireAdmin;
      const nextDetail = detail || (route.path ?? '').split('/').some((s) => s.startsWith(':'));
      if (route.children && route.children.length > 0) {
        walk(route.children, path, nextShell, nextAdmin, nextDetail);
        continue;
      }
      if (!nextShell || nextDetail || route.path === '*' || type === Navigate) continue;
      out.push({ path, meta: readMeta(route), adminOnly: nextAdmin });
    }
  };
  walk(routes, '', false, false, false);
  return out;
}

function hiddenFromStaffFloor(path: string): boolean {
  const navPath = activeNavPath(path, ALL_NAV_PATHS);
  const leaf = ALL_NAV_LEAVES.find((l) => l.path === navPath);
  return leaf !== undefined && !staffSeesLeaf(leaf);
}

/**
 * The screens this session can open, as the assistant's `routes` list: id-free
 * paths, deduplicated, capped at {@link ASK_DINK_ROUTE_LIMIT}.
 */
export function buildAskDinkRoutes(
  routes: readonly RouteObject[],
  access: AskDinkAccess,
): AskDinkRoute[] {
  const adminOpen = canOpenAdminRoutes(access.isAdmin, access.frontDeskView);
  const seen = new Set<string>();
  const out: AskDinkRoute[] = [];
  for (const screen of catalogueScreens(routes)) {
    if (!screen.meta || seen.has(screen.path)) continue;
    if (screen.adminOnly && !adminOpen) continue;
    if (access.isStaffTier && hiddenFromStaffFloor(screen.path)) continue;
    seen.add(screen.path);
    out.push({ path: screen.path, title: screen.meta.title, description: screen.meta.description });
  }
  return out.slice(0, ASK_DINK_ROUTE_LIMIT);
}
